import exifr from "exifr"

// Extracts provenance (where, when, with what) for an uploaded image.
//
// Two callers with deliberately different rules:
//
//   extractPhotoMetadata — documents. EXIF only. Where the *photo* was taken, never where the
//   uploader happens to be sitting.
//
//   extractGeodata — progressions. EXIF first, then the browser Geolocation API, because live
//   camera captures routinely carry no GPS. Anything that came from the browser is tagged
//   geodata_source: "device" so the UI and the API can say so instead of passing it off as a
//   capture location.

const EXIF_SOURCE = "exif"
const DEVICE_SOURCE = "device"

// Long enough to cover one batch of photos on a single permission prompt, short enough that a
// later upload from somewhere else re-asks. Without an expiry the first fix of the session would
// be stamped on every photo uploaded afterwards, hours later and kilometres away.
const POSITION_CACHE_TTL = 2 * 60 * 1000

// getCurrentPosition's own `timeout` does not start until the permission prompt is answered, so
// a user who ignores the prompt would otherwise leave the promise pending forever. Callers await
// this, so it must always settle.
const POSITION_TIMEOUT = 10 * 1000

const EXIF_TAGS = ["DateTimeOriginal", "DateTime", "DateTimeDigitized", "Make", "Model"]

let cachedPosition = null

export async function extractGeodata(file) {
  const [exif, exifCoordinates] = await Promise.all([parseExif(file), extractExifGps(file)])
  const coordinates = exifCoordinates ?? (await getBrowserPosition())

  return {
    latitude: coordinates?.latitude ?? null,
    longitude: coordinates?.longitude ?? null,
    taken_at: takenAt(exif, file),
    geodata_source: coordinates ? (exifCoordinates ? EXIF_SOURCE : DEVICE_SOURCE) : null,
  }
}

export async function extractPhotoMetadata(file) {
  const [exif, coordinates] = await Promise.all([parseExif(file), extractExifGps(file)])

  return {
    latitude: coordinates?.latitude ?? null,
    longitude: coordinates?.longitude ?? null,
    taken_at: exifTakenAt(exif),
    camera: camera(exif),
    geodata_source: coordinates ? EXIF_SOURCE : null,
  }
}

// One pass over the file for every tag we care about. Photos off a phone are 5-15 MB, so parsing
// them once per field is worth avoiding.
async function parseExif(file) {
  try {
    return (await exifr.parse(file, EXIF_TAGS)) ?? null
  } catch (error) {
    console.warn("EXIF parsing failed:", error.message)
    return null
  }
}

async function extractExifGps(file) {
  try {
    const gps = await exifr.gps(file)
    if (gps && Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude)) {
      return { latitude: gps.latitude, longitude: gps.longitude }
    }
  } catch (error) {
    console.warn("EXIF GPS parsing failed:", error.message)
  }
  return null
}

function camera(exif) {
  if (!exif) return null

  const parts = [exif.Make, exif.Model].filter(Boolean).map((value) => String(value).trim())
  return parts.length ? parts.join(" ") : null
}

// The real capture date, or null. A screenshot has no capture date and must not be given one.
function exifTakenAt(exif) {
  if (!exif) return null

  const captureDate = exif.DateTimeOriginal || exif.DateTime || exif.DateTimeDigitized
  return captureDate instanceof Date ? formatDateTime(captureDate) : null
}

// Progressions fall back to the file's own modified-time, which is at least a real property of
// the file. There is deliberately no fallback to "now" — stamping the upload moment as a capture
// time invents provenance rather than recording it.
function takenAt(exif, file) {
  return (
    exifTakenAt(exif) ?? (file.lastModified ? formatDateTime(new Date(file.lastModified)) : null)
  )
}

function getBrowserPosition() {
  if (!("geolocation" in navigator)) return Promise.resolve(null)

  const now = Date.now()
  if (cachedPosition && now - cachedPosition.at < POSITION_CACHE_TTL) {
    return cachedPosition.promise
  }

  const promise = new Promise((resolve) => {
    // Belt and braces against a prompt that is never answered: the spec's timeout only covers
    // acquiring the fix, not the permission dialog in front of it.
    const guard = setTimeout(() => resolve(null), POSITION_TIMEOUT)
    const settle = (value) => {
      clearTimeout(guard)
      resolve(value)
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        settle({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => settle(null),
      { enableHighAccuracy: true, timeout: POSITION_TIMEOUT, maximumAge: 60000 },
    )
  })

  cachedPosition = { promise, at: now }
  return promise
}

const pad = (n) => String(n).padStart(2, "0")
const formatDateTime = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
